//Reports
"use strict";

import "ag-grid-community/styles/ag-grid.css"; // Core CSS
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-enterprise";
import React, { useEffect, useState, useContext } from "react";
import { createPortal } from "react-dom";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DatePicker, { DateObject } from "react-multi-date-picker";
import { api, handleToast } from "../services/config";
import { ToastContainer } from "react-toastify";
import { MagnifyingGlass } from "react-loader-spinner";
import { CSVLink } from "react-csv";
import { FaPrint } from "react-icons/fa6";
import { GiNotebook } from "react-icons/gi";
import { contextApi } from "../context/contextApi";
import PaginationBill from "../components/PaginationBill";
import { CiCircleAlert } from "react-icons/ci";
import { GoAlert } from "react-icons/go";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { toPersianDigits } from "../utils/toPersianDigits";

const numberWithCommas = (x) => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const changeDate = (dateString) => {
  const parts = (dateString || "").split(" ");
  return {
    datePart: toPersianDigits(parts[0] || "-"),
    timePart: toPersianDigits(parts[1] || "-"),
  };
};

const formatAmountFa = (amount) =>
  toPersianDigits(new Intl.NumberFormat("fa-IR").format(amount ?? 0));

const getBeneficiaryName = (person) => {
  if (person?.transactionable?.type === "assetuser") {
    return `${person?.transactionable?.assetuser?.user?.name || ""} ${person?.transactionable?.assetuser?.user?.lastname || ""}`.trim();
  } else if (person?.transactionable?.type === "beneficiary") {
    const name = person?.transactionable?.beneficiary?.name;
    const lastname = person?.transactionable?.beneficiary?.lastname;
    if (!name && !lastname) return "";
    if (name === lastname) return lastname;
    return `${name || ""} ${lastname || ""}`.trim();
  } else {
    return "";
  }
  
};

const getBeneficiarySheba = (person) => {
  if (person?.transactionable?.type === "beneficiary") {
    return person?.transactionable?.beneficiary?.sheba || "-";
  } else if (person?.transactionable?.type === "assetuser") {
    return person?.transactionable?.assetuser?.internal_iban || "-";
  } else {
    return "";
  }
 
};

const getReceiptStatusMeta = (status) => {
  if (status === 100) {
    return {
      label: "تراکنش موفق",
      badgeBg: "#059669",
      accent: "#059669",
      softBg: "#ecfdf5",
      softBorder: "#a7f3d0",
      sealBg: "radial-gradient(circle at 30% 30%, #34d399, #059669 70%)",
      type: "success",
    };
  }
  if (status === 0) {
    return {
      label: "در دست بررسی",
      badgeBg: "#d97706",
      accent: "#d97706",
      softBg: "#fffbeb",
      softBorder: "#fde68a",
      sealBg: "radial-gradient(circle at 30% 30%, #fbbf24, #d97706 70%)",
      type: "pending",
    };
  }
  if (status === -100) {
    return {
      label: "تراکنش ناموفق",
      badgeBg: "#dc2626",
      accent: "#dc2626",
      softBg: "#fef2f2",
      softBorder: "#fecaca",
      sealBg: "radial-gradient(circle at 30% 30%, #f87171, #dc2626 70%)",
      type: "failed",
    };
  }
  return {
    label: "",
    badgeBg: "#64748b",
    accent: "#64748b",
    softBg: "#f8fafc",
    softBorder: "#e2e8f0",
    sealBg: "radial-gradient(circle at 30% 30%, #94a3b8, #64748b 70%)",
    type: "default",
  };
};

const ReceiptPerforation = ({ compact }) => (
  <div
    style={{
      position: "relative",
      height: compact ? "10px" : "14px",
      margin: compact ? "2px 0" : "4px 0",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        top: "50%",
        borderTop: "2px dashed #cbd5e1",
      }}
    />
    <div
      style={{
        position: "absolute",
        left: "-8px",
        top: "50%",
        transform: "translateY(-50%)",
        width: compact ? "12px" : "16px",
        height: compact ? "12px" : "16px",
        borderRadius: "50%",
        background: "#e2e8f0",
      }}
    />
    <div
      style={{
        position: "absolute",
        right: "-8px",
        top: "50%",
        transform: "translateY(-50%)",
        width: compact ? "12px" : "16px",
        height: compact ? "12px" : "16px",
        borderRadius: "50%",
        background: "#e2e8f0",
      }}
    />
  </div>
);

const ReceiptSectionTitle = ({ children, compact }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      margin: compact ? "8px 0 4px" : "14px 0 8px",
    }}
  >
    <span
      style={{
        width: "3px",
        height: compact ? "12px" : "14px",
        borderRadius: "2px",
        background: "#0010c6",
        flexShrink: 0,
      }}
    />
    <span
      style={{
        fontSize: compact ? "10px" : "11px",
        fontWeight: 700,
        color: "#0010c6",
        letterSpacing: "0.02em",
      }}
    >
      {children}
    </span>
    <span
      style={{
        flex: 1,
        height: "1px",
        background: "linear-gradient(to left, transparent, #e2e8f0)",
      }}
    />
  </div>
);

const ReceiptDetailRow = ({ label, value, mono, last, compact }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "12px",
      padding: compact ? "5px 0" : "9px 0",
      borderBottom: last ? "none" : "1px solid #f1f5f9",
    }}
  >
    <span
      style={{
        color: "#94a3b8",
        fontSize: compact ? "11px" : "12px",
        whiteSpace: "nowrap",
        paddingTop: "1px",
      }}
    >
      {label}
    </span>
    <span
      style={{
        color: "#0f172a",
        fontSize: mono ? (compact ? "10px" : "11px") : compact ? "11.5px" : "12.5px",
        fontWeight: 600,
        textAlign: "left",
        direction: mono ? "ltr" : "rtl",
        fontFamily: mono
          ? "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"
          : "Vazir, Tahoma, sans-serif",
        wordBreak: "break-all",
        lineHeight: 1.4,
        maxWidth: "62%",
      }}
    >
      {value}
    </span>
  </div>
);

/** رسید فوق‌حرفه‌ای برداشت — مشترک برای مودال، چاپ و رسید عمومی */
const ProfessionalWithdrawalReceipt = ({
  data,
  profile,
  mode = "screen",
  title = "رسید برداشت",
  onClose,
  onPublic,
  onPrint,
}) => {
  const statusMeta = getReceiptStatusMeta(data?.status);
  const { datePart, timePart } = changeDate(data?.created_at);
  const isPublic = mode === "public";
  const isScreen = mode === "screen";
  // چاپ و رسید عمومی هم فشرده تا در یک صفحه جا شوند
  const compact = true;
  const sidePad = isScreen ? "18px" : "16px";
  const destName = getBeneficiaryName(data);
  const destSheba = getBeneficiarySheba(data);
  const bankName =
    data?.transactionable?.beneficiary?.bank_name || "بانک";
  const profileName = `${profile?.name || ""} ${profile?.lastname || ""}`.trim();
  const trackingId = isPublic ? data?.txid || "-" : data?.bank_txid || "-";

  return (
    <div
      style={{
        width: "100%",
        background: "#fff",
        borderRadius: isScreen ? "16px" : "14px",
        overflow: "hidden",
        fontFamily: "Vazir, Tahoma, sans-serif",
        direction: "rtl",
        boxShadow: isScreen
          ? "0 25px 50px -12px rgba(0, 16, 198, 0.25), 0 0 0 1px rgba(15, 23, 42, 0.06)"
          : "none",
        border: isScreen ? "none" : "1px solid #e2e8f0",
        position: "relative",
      }}
    >
      {/* Header */}
      <div
        style={{
          position: "relative",
          background:
            "linear-gradient(135deg, #000a7a 0%, #0010c6 45%, #1e3a8a 100%)",
          padding: compact
            ? `12px ${sidePad} 36px`
            : `16px ${sidePad} 44px`,
          color: "#fff",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle at 12% 20%, rgba(255,255,255,0.12) 0, transparent 42%), radial-gradient(circle at 88% 10%, rgba(34,197,94,0.18) 0, transparent 35%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: compact ? "36px" : "42px",
                height: compact ? "36px" : "42px",
                borderRadius: "10px",
                background: "rgba(255,255,255,0.95)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "4px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
            >
              <img
                src="/images/logo.png"
                alt="logo"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>
            <div>
              <div
                style={{
                  fontSize: compact ? "14px" : "15px",
                  fontWeight: 800,
                  letterSpacing: "-0.01em",
                }}
              >
                {title}
              </div>
              <div
                style={{
                  fontSize: "10px",
                  opacity: 0.75,
                  marginTop: "1px",
                }}
              >
                سند رسمی تراکنش مالی
              </div>
            </div>
          </div>
          {isScreen && onClose && (
            <button
              type="button"
              onClick={onClose}
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "999px",
                border: "1px solid rgba(255,255,255,0.35)",
                background: "rgba(255,255,255,0.1)",
                color: "#fff",
                cursor: "pointer",
                fontSize: "15px",
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-label="بستن"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Floating status seal */}
      <div
        style={{
          marginTop: compact ? "-26px" : "-34px",
          display: "flex",
          justifyContent: "center",
          position: "relative",
          zIndex: 2,
        }}
      >
        <div
          style={{
            width: compact ? "52px" : "68px",
            height: compact ? "52px" : "68px",
            borderRadius: "50%",
            background: statusMeta.sealBg,
            boxShadow: `0 8px 18px ${statusMeta.accent}55, 0 0 0 5px #fff`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
          }}
        >
          {statusMeta.type === "success" && (
            <IoIosCheckmarkCircle
              style={{ fontSize: compact ? "30px" : "38px", color: "#fff" }}
            />
          )}
          {statusMeta.type === "pending" && (
            <GoAlert
              style={{ fontSize: compact ? "26px" : "32px", color: "#fff" }}
            />
          )}
          {statusMeta.type === "failed" && (
            <CiCircleAlert
              style={{ fontSize: compact ? "28px" : "36px", color: "#fff" }}
            />
          )}
        </div>
      </div>

      <div
        style={{
          padding: compact
            ? `4px ${sidePad} 12px`
            : `6px ${sidePad} 18px`,
        }}
      >
        <div style={{ textAlign: "center", marginBottom: compact ? "2px" : "6px" }}>
          <div
            style={{
              fontSize: compact ? "14px" : "16px",
              fontWeight: 800,
              color: statusMeta.accent,
              marginTop: "2px",
            }}
          >
            {statusMeta.label}
          </div>
     
        </div>

        <ReceiptPerforation compact={compact} />

        {/* Amount hero */}
        <div
          style={{
            textAlign: "center",
            padding: compact ? "8px 10px 10px" : "12px 14px 14px",
            background:
              "linear-gradient(180deg, #f8faff 0%, #ffffff 55%, #f1f5f9 100%)",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            position: "relative",
          }}
        >
          <div
            style={{
              fontSize: "10.5px",
              color: "#64748b",
              marginBottom: "3px",
              fontWeight: 500,
            }}
          >
            {isPublic ? "مبلغ کل تراکنش" : "مبلغ برداشت"}
          </div>
          <div
            style={{
              fontSize: compact ? "22px" : "24px",
              fontWeight: 900,
              color: "#0f172a",
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatAmountFa(data?.amount)}
            <span
              style={{
                marginRight: "6px",
                fontSize: "12px",
                fontWeight: 700,
                color: "#0010c6",
              }}
            >
              ریال
            </span>
          </div>
        </div>

        <ReceiptSectionTitle compact={compact}>اطلاعات تراکنش</ReceiptSectionTitle>
        <div
          style={{
            background: "#fff",
            border: "1px solid #f1f5f9",
            borderRadius: "10px",
            padding: compact ? "1px 12px" : "4px 16px",
          }}
        >
          {isPublic ? (
            <>
              <ReceiptDetailRow
                compact={compact}
                label="مبلغ تسویه شده"
                value={`${formatAmountFa(data?.amount)} ریال`}
              />
              <ReceiptDetailRow
                compact={compact}
                label="تاریخ / ساعت"
                value={`${datePart}  ·  ${timePart}`}
              />
              <ReceiptDetailRow
                compact={compact}
                label="وضعیت تراکنش"
                value={data?.status_text || "-"}
                last
              />
            </>
          ) : (
            <>
              <ReceiptDetailRow
                compact={compact}
                label="وضعیت"
                value={data?.status_text || "-"}
              />
              <ReceiptDetailRow
                compact={compact}
                label="تاریخ / ساعت"
                value={`${datePart}  ·  ${timePart}`}
                last={compact}
              />
              {!compact && (
                <ReceiptDetailRow
                  compact={compact}
                  label="مبلغ"
                  value={formatAmountFa(data?.amount)}
                  last
                />
              )}
            </>
          )}
        </div>

        <ReceiptSectionTitle compact={compact}>
          {isPublic ? "اطلاعات مقصد" : "مبدأ و مقصد"}
        </ReceiptSectionTitle>
        <div
          style={{
            background: "#fff",
            border: "1px solid #f1f5f9",
            borderRadius: "10px",
            padding: compact ? "1px 12px" : "4px 16px",
          }}
        >
          {!isPublic && (
            <ReceiptDetailRow
              compact={compact}
              label="نام مبدا"
              value={profileName || "-"}
            />
          )}
          <ReceiptDetailRow
            compact={compact}
            label={isPublic ? "نام حساب مقصد" : "نام مقصد"}
            value={destName || "-"}
          />
          {isPublic && (
            <ReceiptDetailRow
              compact={compact}
              label="بانک مقصد"
              value={bankName}
            />
          )}
             <ReceiptDetailRow
            compact={compact}
            label={isPublic ? "شبای مقصد" : "شماره شبا مقصد"}
            value={
              isPublic
                ? data?.transactionable?.type === "beneficiary"
                  ? data?.transactionable?.beneficiary?.sheba || "-"
                  : data?.transactionable?.type === "assetuser"
                  ? data?.transactionable?.assetuser?.internal_iban || "-"
                  : "-"
                : destSheba
            }
            mono
            last
          />
          {!isPublic && (
            <ReceiptDetailRow
              compact={compact}
              label="شناسه کیف پول مبدا"
              value={toPersianDigits(data?.asset_id ?? "-")}
            />
          )}
       
        </div>

        <ReceiptSectionTitle compact={compact}>
          {isPublic ? "شناسه پیگیری بانکی" : "کد رهگیری بانکی"}
        </ReceiptSectionTitle>
        <div
          style={{
            borderRadius: "10px",
            border: "1.5px dashed #cbd5e1",
            background: "#f8fafc",
            padding: compact ? "8px 12px" : "12px 16px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
              fontSize: compact ? "11px" : "12px",
              fontWeight: 700,
              color: "#0f172a",
              direction: "ltr",
              letterSpacing: "0.04em",
              wordBreak: "break-all",
              lineHeight: 1.45,
            }}
          >
            {trackingId}
          </div>
        </div>

        <ReceiptPerforation compact={compact} />

        <div
          style={{
            textAlign: "center",
            color: "#94a3b8",
            fontSize: "10px",
            lineHeight: 1.5,
            padding: compact ? "0 4px" : "2px 12px 4px",
          }}
        >
          این رسید به منزله تأیید انجام تراکنش می‌باشد
          {!compact && (
            <>
              <br />
              لطفاً تا زمان تأیید نهایی نزد خود نگه دارید
            </>
          )}
        </div>

        {isScreen && (onPublic || onPrint) && (
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginTop: "10px",
            }}
          >
            {onPublic && (
              <button
                type="button"
                onClick={onPublic}
                style={{
                  flex: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  border: "none",
                  borderRadius: "10px",
                  background:
                    "linear-gradient(135deg, #0010c6 0%, #1e40af 100%)",
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: 700,
                  padding: "9px 10px",
                  cursor: "pointer",
                  boxShadow: "0 6px 14px rgba(0, 16, 198, 0.25)",
                  fontFamily: "inherit",
                }}
              >
                <GiNotebook />
                رسید عمومی
              </button>
            )}
            {onPrint && (
              <button
                type="button"
                onClick={onPrint}
                style={{
                  flex: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  background: "#0f172a",
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: 700,
                  padding: "9px 10px",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                <FaPrint />
                چاپ
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const ReportBill = (props) => {
  const context = useContext(contextApi);
  const [fromDate, setFromDate] = useState(new DateObject());
  const [toDate, setToDate] = useState(new DateObject());
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [selectedBag, setSelectedBag] = useState(null);
  const [typeTransaction, setTypeTransaction] = useState("");
  const [data, setData] = useState([]);
  const [data2, setData2] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [modalOpen2, setModalOpen2] = useState(false);
  const [selectedId2, setSelectedId2] = useState(null);
  const [profile, setProfile] = useState(
    JSON.parse(localStorage.getItem("profile"))
  );
  const [loading2, setLoading2] = useState(true);
  const [pageCount, setPageCount] = useState(1);
  const [meta, setMeta] = useState({});
  const [appliedFilters, setAppliedFilters] = useState(null); // برای نگه‌داری فیلترهای اعمال شده

  const handleMinPriceChange = (event) => {
    const value = event.target.value.replace(/\D/g, "");
    const formattedValue = numberWithCommas(value);

    setMinPrice(formattedValue);
  };

  const toggleModal = (id) => {
    setSelectedId(id);
    setModalOpen(!modalOpen);
  };
  const toggleModal2 = (id) => {
    setSelectedId2(id);
    setModalOpen2(!modalOpen2);
  };

  const handleMaxPriceChange = (event) => {
    const value = event.target.value.replace(/\D/g, "");
    const formattedValue = numberWithCommas(value);

    setMaxPrice(formattedValue);
  };

  const handleChangeFirstSelect = (e) => {
    const selectedValue = e.target.value;
    setSelectedBag(selectedValue);
  };

  const formatDate = (dateObj) => {
    const year = dateObj.year;
    const month = dateObj.month;
    const day = dateObj.day;
    const formattedDate = `${year}-${month}-${day}`;

    return formattedDate;
  };

  // فرمت تاریخ برای نمایش (1404/09/30)
  const formatDateForDisplay = (dateObj) => {
    if (!dateObj || !dateObj.year || !dateObj.month || !dateObj.day) {
      return "";
    }
    const year = dateObj.year;
    const month = String(dateObj.month).padStart(2, "0");
    const day = String(dateObj.day).padStart(2, "0");
    return `${year}/${month}/${day}`;
  };

  // پیدا کردن نام کیف پول بر اساس id
  const getWalletName = (walletId) => {
    const wallets = {
      1: "کیف پول ریالی pos",
      2: "کیف پول ریالی لینک",
      3: "کیف پول ریالی واریز به حساب",
      4: "کیف پول ریالی چک",
      5: "کیف پول ریالی vip",
      6: "کیف پول ریالی کارت اعتباری",
    };
    return wallets[walletId] || "";
  };

  // پیدا کردن نام نوع تراکنش
  const getTransactionTypeName = (type) => {
    const types = {
      "-1": "برداشت",
      1: "واریز",
    };
    return types[type] || "";
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await api.get(`transactions?type=beneficiary`, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            authorization: `Bearer ${localStorage.getItem("login")}`,
          },
        });
        console.log(response.data.data);
        setData(response.data.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
      }
    };
    const fetchData2 = async () => {
      setLoading(true);
      try {
        const response = await api.get(
          `transactions?[related_to]=11&&page=${pageCount}`,
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              authorization: `Bearer ${localStorage.getItem("login")}`,
            },
          }
        );
        setData2(response.data.data);
        setMeta(response.data.meta);
      } catch (error) {
        console.log(error);
      }
    };

    fetchData();
    fetchData2();
  }, [pageCount]);

  const handleSearch = async () => {
    handleClick("  جستجو کردن در صورتحساب کلیک شد");

    // بررسی حداقل و حداکثر مبلغ - اگر یکی پر شده، هر دو باید پر باشند
    if ((minPrice && !maxPrice) || (!minPrice && maxPrice)) {
      handleToast("error", "لطفا حداقل و حداکثر قیمت را انتخاب کنید");
      return;
    }

    // بررسی تاریخ - اگر یکی انتخاب شده، هر دو باید انتخاب شده باشند
    const fromDateStr = formatDate(fromDate);
    const toDateStr = formatDate(toDate);
    const hasFromDate =
      fromDate && fromDate.year && fromDate.month && fromDate.day;
    const hasToDate = toDate && toDate.year && toDate.month && toDate.day;

    if ((hasFromDate && !hasToDate) || (!hasFromDate && hasToDate)) {
      handleToast("error", "بازه تاریخ را انتخاب کنید");
      return;
    }

    // ساخت URL به صورت داینامیک بر اساس فیلترهای انتخاب شده
    let url = "transactions?";
    const params = [];

    // اضافه کردن asset_id اگر انتخاب شده باشد
    if (selectedBag) {
      params.push(`[asset_id]=${selectedBag}`);
    }

    // اضافه کردن بازه تاریخ اگر هر دو انتخاب شده باشند
    if (hasFromDate && hasToDate) {
      params.push(`f[created_at_between]=${fromDateStr},${toDateStr}`);
    }

    // اضافه کردن بازه مبلغ اگر هر دو پر شده باشند
    if (minPrice && maxPrice) {
      // حذف کاما از مبالغ برای ارسال به API
      const minPriceNum = minPrice.replace(/,/g, "");
      const maxPriceNum = maxPrice.replace(/,/g, "");
      params.push(`f[amount][min]=${minPriceNum}`);
      params.push(`f[amount][max]=${maxPriceNum}`);
    }

    // اضافه کردن نوع تراکنش اگر انتخاب شده باشد
    if (typeTransaction && typeTransaction !== "") {
      if (typeTransaction === "11") {
        params.push(`f[related_to]=11`);
      } else {
        params.push(`f[type]=${typeTransaction}`);
      }
    }

    // اگر هیچ فیلتری انتخاب نشده باشد
    if (params.length === 0) {
      handleToast("error", "لطفا حداقل یک فیلتر را انتخاب کنید");
      return;
    }

    url += params.join("&&");

    setLoading(true);
    try {
      const response = await api.get(url, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${localStorage.getItem("login")}`,
        },
      });

      setData2(response.data.data);
      setMeta(response.data.meta);

      // ذخیره فیلترهای اعمال شده برای نمایش
      const filters = {};
      if (selectedBag) {
        filters.walletName = getWalletName(selectedBag);
      }
      if (typeTransaction && typeTransaction !== "") {
        if (typeTransaction === "11") {
          filters.transactionType = "تبدیل";
        } else {
          filters.transactionType = getTransactionTypeName(typeTransaction);
        }
      }
      if (minPrice && maxPrice) {
        filters.minPrice = minPrice;
        filters.maxPrice = maxPrice;
      }
      if (hasFromDate && hasToDate) {
        filters.fromDate = formatDateForDisplay(fromDate);
        filters.toDate = formatDateForDisplay(toDate);
      }
      setAppliedFilters(filters);

      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
      handleToast("error", "خطا در دریافت اطلاعات");
    }
  };

  const handleClearFilters = async () => {
    handleClick("پاک کردن فیلترها در صورتحساب کلیک شد");

    // پاک کردن همه فیلترها
    setMinPrice("");
    setMaxPrice("");
    setSelectedBag(null);
    setTypeTransaction("");
    setFromDate(new DateObject());
    setToDate(new DateObject());
    setAppliedFilters(null); // پاک کردن فیلترهای اعمال شده

    // برگرداندن داده‌ها به حالت اولیه
    setLoading(true);
    try {
      const response = await api.get(`transactions?[related_to]=11&&page=1`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${localStorage.getItem("login")}`,
        },
      });
      setData2(response.data.data);
      setMeta(response.data.meta);
      setPageCount(1);
      setLoading(false);
      handleToast("success", "فیلترها پاک شدند");
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleExcelExport = async () => {
    setLoading(true);
    handleToast("success", "فایل در آماده سازی ");
    try {
      // build query params only for filled filters to avoid sending empty values
      const params = [];

      if (selectedBag) {
        params.push(`f[asset_id]=${selectedBag}`);
      }

      const hasFromDate =
        fromDate && fromDate.year && fromDate.month && fromDate.day;
      const hasToDate = toDate && toDate.year && toDate.month && toDate.day;
      if (hasFromDate && hasToDate) {
        params.push(
          `f[created_at_between]=${formatDate(fromDate)},${formatDate(toDate)}`
        );
      }

      if (minPrice && maxPrice) {
        const minPriceNum = minPrice.replace(/,/g, "");
        const maxPriceNum = maxPrice.replace(/,/g, "");
        params.push(`f[amount]=${minPriceNum}-${maxPriceNum}`);
      }

      if (typeTransaction) {
        params.push(`f[type]=${typeTransaction}`);
      }

      params.push("per_page=25");
      params.push("excel=1");

      const queryString = params.join("&");
      const response = await api.get(`transactions?${queryString}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${localStorage.getItem("login")}`,
        },
      });
      setData(response.data.data);
      handleClick("خروجی اکسل در صورتحساب کلیک شد");
      handleToast("success", "جهت دانلود به صفحه اکسل ها مراجعه کنید!");
    } catch (error) {
      console.log("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExcelExportAll = async () => {
    setLoading(true);
    handleToast("success", "فایل در آماده سازی ");
    try {
      // build query params only for filled filters to avoid sending empty values
      const params = [];

      params.push("per_page=25");
      params.push("excel=1");

      const queryString = params.join("&");
      const response = await api.get(`transactions?${queryString}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          authorization: `Bearer ${localStorage.getItem("login")}`,
        },
      });
      setData(response.data.data);
      handleClick("خروجی اکسل در صورتحساب کلیک شد");
      handleToast("success", "جهت دانلود به صفحه اکسل ها مراجعه کنید!");
    } catch (error) {
      console.log("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const [printData, setPrintData] = useState(null);
  const [printModalData, setPrintModalData] = useState(null);

  const printReceipt = (person) => {
    setPrintModalData(null);
    setPrintData(person);
    document.body.classList.add("printing-receipt");

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setPrintData(null);
      document.body.classList.remove("printing-receipt");
      window.removeEventListener("afterprint", finish);
    };
    window.addEventListener("afterprint", finish);

    setTimeout(() => {
      window.print();
      setTimeout(finish, 1000);
    }, 150);
  };

  const printModalReceipt = (person) => {
    setPrintData(null);
    setPrintModalData(person);
    document.body.classList.add("printing-receipt");

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      setPrintModalData(null);
      document.body.classList.remove("printing-receipt");
      window.removeEventListener("afterprint", finish);
    };
    window.addEventListener("afterprint", finish);

    setTimeout(() => {
      window.print();
      setTimeout(finish, 1000);
    }, 150);
  };

  const handleClick = (buttonName) => {
    context.recordClick(buttonName);
  };

  const [isDark, setIsDark] = useState(
    () =>
      typeof window !== "undefined" && document.body.classList.contains("dark")
  );

  useEffect(() => {
    const checkDark = () => setIsDark(document.body.classList.contains("dark"));
    checkDark();
    const observer = new MutationObserver(() => {
      checkDark();
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => {
      observer.disconnect();
    };
  }, []);


 
  return (
    <div className="w-full h-screen bg-white dark:bg-[#0D1117]">
      <div className="mb-6 p-3 lg:p-6">
        <h1
          className="
    relative inline-block text-3xl font-bold mb-6 
    text-[#0010c6] dark:text-white
    after:content-[''] after:block after:w-full after:h-[4px] 
    after:bg-[#0010c6] after:mt-[10px] dark:after:bg-[#ffffff]
  "
        >
          صورتحساب
        </h1>
      </div>
      <div className=" flex flex-col">
        <ToastContainer />
        <div className="w-[95%] mt-10 mx-auto flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#0010c6] dark:text-white">
            اعمال فیلتر
          </h3>

          <button
            onClick={handleExcelExportAll}
            className="border border-1 border-gray-600 text-sm bg-green-600 hover:bg-green-700 px-3 py-1 rounded-lg text-yellow-50"
            disabled={loading}
          >
            خروجی اکسل کلی
          </button>
        </div>
        <div
          className={`w-[95%] mx-auto mt-10 border px-1 py-4 border-1 border-gray-300  rounded-lg bg-[#E8F0FE] dark:bg-[#161B22]`}
        >
          <div className="flex flex-col md:flex-row items-center gap-2 w-full justify-center">
            <div className="w-full md:w-1/2">
              <label className="text-sm block text-[#0010c6] dark:text-white">
                نام کیف پول :{" "}
              </label>
              <select
                value={selectedBag || ""}
                onChange={handleChangeFirstSelect}
                className="my-3 mx-auto text-black bg-gray-100 dark:bg-gray-400 border rounded-md px-2 py-1 text-xs font-normal outline-none w-full  h-auto"
              >
                <option value="" hidden>
                  انتخاب کنید
                </option>
                <option value="1">کیف پول ریالی pos</option>
                <option value="2">کیف پول ریالی لینک</option>
                <option value="3">کیف پول ریالی واریز به حساب</option>
                <option value="4">کیف پول ریالی چک</option>
                <option value="6">کیف پول ریالی کارت اعتباری</option>
                <option value="5">کیف پول ریالی vip</option>
              </select>
            </div>
            <div className="w-full md:w-1/2">
              <span className="text-sm block text-[#0010c6] dark:text-white">
                نوع تراکنش :{" "}
              </span>
              <select
                value={typeTransaction}
                onChange={(e) => setTypeTransaction(e.target.value)}
                className=" mx-auto text-black bg-gray-100 dark:bg-gray-400 border rounded-md px-2 my-3  py-[3px] text-xs  font-normal outline-none w-full h-auto"
              >
                <option value="">همه</option>
                <option value="-1">برداشت</option>
                <option value="1">واریز</option>
                <option value="11">تبدیل</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-2 w-full justify-center mt-5">
            <div className="w-full md:w-1/2">
              <span className="text-sm block py-2 text-[#0010c6] dark:text-white">
                {" "}
                حداقل مبلغ :{" "}
              </span>
              <input
                type="text"
                name="minPrice"
                value={minPrice}
                onChange={handleMinPriceChange}
                className="w-full text-sm px-2 py-1 rounded-lg border border-1 border-gray-400 text-black bg-gray-100 dark:bg-gray-400"
              />
            </div>
            <div className="w-full md:w-1/2">
              <span className="text-sm block py-2 text-[#0010c6] dark:text-white">
                {" "}
                حداکثر مبلغ :{" "}
              </span>
              <input
                type="text"
                name="maxPrice"
                value={maxPrice}
                onChange={handleMaxPriceChange}
                className="w-full text-black bg-gray-100 dark:bg-gray-400 px-2 text-sm py-1 rounded-lg border border-1 border-gray-400"
              />
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-end gap-2 w-full justify-center mt-5">
            <div className="dateBox w-full md:w-1/3">
              <span className="text-sm block text-[#0010c6] dark:text-white">
                از تاریخ :{" "}
              </span>

              <DatePicker
                calendar={persian}
                locale={persian_fa}
                value={fromDate}
                onChange={setFromDate}
                calendarPosition="bottom-right"
                inputClass="text-black bg-gray-100  dark:bg-gray-400 w-full text-sm p-[13px_15px] border-none focus:ring-0"
                containerClassName="w-full"
                style={{
                  width: "100%",
                  fontSize: "14px",
                  padding: "9px 15px",
                  borderRadius: "10px",
                  marginTop: "10px",
                  direction: "ltr",
                  border:
                    "1px solid rgb(156 163 175 / var(--tw-border-opacity))",
                }}
              />
            </div>
            <div className="w-full md:w-1/3">
              <span className="text-sm block text-[#0010c6] dark:text-white">
                تا تاریخ :{" "}
              </span>
              <DatePicker
                calendar={persian}
                locale={persian_fa}
                value={toDate}
                onChange={setToDate}
                calendarPosition="bottom-right"
                inputClass="text-black bg-gray-100 dark:bg-gray-400 w-full text-sm p-[13px_15px] border-none focus:ring-0"
                containerClassName="w-full"
                style={{
                  width: "100%",
                  fontSize: "14px",
                  padding: "9px 15px",
                  borderRadius: "10px",
                  marginTop: "10px",
                  direction: "ltr",
                  border:
                    "1px solid rgb(156 163 175 / var(--tw-border-opacity))",
                }}
              />
            </div>
            <div className="w-full md:w-1/3 gap-1 flex justify-end items-center">
              <button
                onClick={handleExcelExport}
                className="border border-1 border-gray-600 text-sm bg-green-600 hover:bg-green-700 px-3 py-1 rounded-lg text-yellow-50"
                disabled={loading}
              >
                خروجی اکسل براساس فیلتر
              </button>

              <button
                type="button"
                className="border border-1 border-gray-600 text-sm bg-red-600 hover:bg-red-700 px-3 py-1 rounded-lg text-yellow-50"
                onClick={handleClearFilters}
                disabled={loading}
              >
                پاک کردن فیلترها
              </button>

              <button
                type="button"
                className="border border-1 border-gray-600 text-sm bg-green-600 hover:bg-green-700 px-3 py-1 rounded-lg text-yellow-50"
                onClick={handleSearch}
              >
                جستجو کردن
              </button>
            </div>
          </div>
        </div>

        <div className="w-[95%] mx-auto md:w-full">
          <div className="px-4 sm:px-6 lg:px-8">
            {/* نمایش فیلترهای اعمال شده */}
            {appliedFilters && (
              <div className="mt-8 mb-4 p-4 bg-blue-50 dark:bg-[#1E2329] rounded-lg border border-blue-200 dark:border-gray-700">
                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-semibold text-[#0010c6] dark:text-white">
                    فیلتر براساس:
                  </span>
                  {appliedFilters.walletName && (
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 rounded-md">
                      نام کیف پول {appliedFilters.walletName}
                    </span>
                  )}
                  {appliedFilters.transactionType && (
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 rounded-md">
                      نوع تراکنش {appliedFilters.transactionType}
                    </span>
                  )}
                  {appliedFilters.minPrice && appliedFilters.maxPrice && (
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 rounded-md">
                      حداقل مبلغ {appliedFilters.minPrice} و حداکثر{" "}
                      {appliedFilters.maxPrice} تومان
                    </span>
                  )}
                  {appliedFilters.fromDate && appliedFilters.toDate && (
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 rounded-md">
                      از تاریخ {appliedFilters.fromDate} تا تاریخ{" "}
                      {appliedFilters.toDate}
                    </span>
                  )}
                  <span className="text-green-600 dark:text-green-400 font-semibold">
                    اعمال شد
                  </span>
                </div>
              </div>
            )}
            <div className="mt-8 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg bg-white dark:bg-[#161B22]">
                    <table className="min-w-full border-collapse border border-gray-300 dark:border-gray-700">
                      <thead className="bg-[#E8F0FE] dark:bg-[#1E2329]">
                        <tr>
                          <th
                            scope="col"
                            className="py-4 pl-4 pr-3 text-center text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-300 sm:pl-6 border border-gray-300 dark:border-gray-700"
                          >
                            تاریخ
                          </th>
                          <th
                            scope="col"
                            className="py-4 pl-4 pr-3 text-center text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-300 sm:pl-6 border border-gray-300 dark:border-gray-700"
                          >
                            ساعت
                          </th>
                          <th
                            scope="col"
                            className="py-4 pl-4 pr-3 text-center text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-300 sm:pl-6 border border-gray-300 dark:border-gray-700"
                          >
                            مبلغ(ریال)
                          </th>
                          <th
                            scope="col"
                            className="py-4 pl-4 pr-3 text-center text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-300 sm:pl-6 border border-gray-300 dark:border-gray-700"
                          >
                            نام کیف پول
                          </th>
                          <th
                            scope="col"
                            className="py-4 pl-4 pr-3 text-center text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-300 sm:pl-6 border border-gray-300 dark:border-gray-700"
                          >
                            نوع تراکنش
                          </th>
                          <th
                            scope="col"
                            className="py-4 pl-4 pr-3 text-center text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-300 sm:pl-6 border border-gray-300 dark:border-gray-700"
                          >
                           شرح تراکنش
                          </th>
                          <th
                            scope="col"
                            className="py-4 pl-4 pr-3 text-center text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-300 sm:pl-6 border border-gray-300 dark:border-gray-700"
                          >
                            فی شبکه
                          </th>
                          <th
                            scope="col"
                            className="py-4 pl-4 pr-3 text-center text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-300 sm:pl-6 border border-gray-300 dark:border-gray-700"
                          >
                            کد رهگیری بانکی
                          </th>
                          <th
                            scope="col"
                            className="py-4 pl-4 pr-3 text-center text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-300 sm:pl-6 border border-gray-300 dark:border-gray-700"
                          >
                            صاحب حساب مقصد
                          </th>
                          <th
                            scope="col"
                            className="py-4 pl-4 pr-3 text-center text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-300 sm:pl-6 border border-gray-300 dark:border-gray-700"
                          >
                            وضعیت
                          </th>
                          <th
                            scope="col"
                            className="py-4 pl-4 pr-3 text-center text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-300 sm:pl-6 border border-gray-300 dark:border-gray-700"
                          >
                            رسید
                          </th>

                          <th
                            scope="col"
                            className="py-4 pl-4 pr-3 text-center text-xs md:text-sm font-semibold text-gray-900 dark:text-gray-300 sm:pl-6 border border-gray-300 dark:border-gray-700"
                          >
                            توضیحات
                          </th>
                         
                        </tr>
                      </thead>
                      {loading ? (
                        <div className="flex justify-center items-center mt-8 w-full">
                          <MagnifyingGlass
                            visible={true}
                            height="80"
                            width="80"
                            ariaLabel="magnifying-glass-loading"
                            wrapperStyle={{}}
                            wrapperClass="magnifying-glass-wrapper"
                            glassColor="#c0efff"
                            color="#e15b64"
                          />
                        </div>
                      ) : (
                        <tbody>
                          {data2?.length > 0 &&
                            data2?.map((person, index) => (
                              <tr
                                key={person.id}
                                className={`${
                                  index % 2 === 0
                                    ? "bg-white dark:bg-[#1E2329]" // زوج
                                    : "bg-[#eef6ff] dark:bg-[#161B22]" // فرد
                                }`}
                              >
                                <td className="whitespace-nowrap px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-200 text-center border border-gray-300 dark:border-gray-700">
                                  {changeDate(person.created_at).datePart}
                                </td>

                                <td className="whitespace-nowrap px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-200 text-center border border-gray-300 dark:border-gray-700">
                                  {changeDate(person.created_at).timePart}
                                </td>

                                <td className="whitespace-nowrap px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-200 text-center border border-gray-300 dark:border-gray-700">
                                  {formatAmountFa(person.amount)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-200 text-center border border-gray-300 dark:border-gray-700">
                                  {toPersianDigits(person.asset.name)}
                                </td>

                                <td className="whitespace-nowrap px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-200 text-center border border-gray-300 dark:border-gray-700">
                                  {toPersianDigits(person.type_text)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-200 text-center border border-gray-300 dark:border-gray-700">
                                  {toPersianDigits(person?.related_to_text)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-200 text-center border border-gray-300 dark:border-gray-700">
                                  {toPersianDigits(
                                    Math.abs(
                                      Number(person.amount) -
                                        Number(person.amount_after_fee)
                                    ).toLocaleString("en-US")
                                  )}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-200 text-center border border-gray-300 dark:border-gray-700">
                                  {toPersianDigits(person.bank_txid || "-")}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-200 text-center border border-gray-300 dark:border-gray-700">
                                  {toPersianDigits(getBeneficiaryName(person))}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-200 text-center border border-gray-300 dark:border-gray-700">
                                  {toPersianDigits(person.status_text)}
                                </td>
                                <td className="whitespace-nowrap px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-200 text-center border border-gray-300 dark:border-gray-700">
                                  {true  ? (
                                    <span
                                      onClick={() => toggleModal2(person.id)}
                                      className="text-blue-500 cursor-pointer"
                                    >
                                      دریافت رسید
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </td>

                                <td className="whitespace-nowrap px-3 py-4 text-xs md:text-sm text-gray-900 dark:text-gray-200 text-center border border-gray-300 dark:border-gray-700">
                                  <span
                                    onClick={() => toggleModal(person.id)}
                                    className="text-blue-500 cursor-pointer"
                                  >
                                    مشاهده
                                  </span>
                                </td>
                                <td >
                                  {modalOpen && selectedId === person.id && (
                                    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex justify-center items-center">
                                      <div className="bg-white py-4 px-4 rounded-lg w-3/4 md:w-2/4 mx-auto">
                                        <div className="flex items-center justify-end w-full pb-5">
                                          <button
                                            onClick={() =>
                                              toggleModal(person.id)
                                            }
                                            className="w-6 h-6 cursor-pointer rounded-full bg-slate-800 text-white flex items-center justify-center"
                                          >
                                            x
                                          </button>
                                        </div>
                                        <p className="py-8">
                                          {toPersianDigits(
                                            person.status === -1 ||
                                              person.status === -100
                                              ? person?.ext?.reject_reason
                                              : person.des
                                          )}
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                </td>
                                <td >
                                  {modalOpen2 && selectedId2 === person.id && (
                                    <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-[6px] flex justify-center items-center p-3">
                                      <div className="w-[96%] max-w-[560px] mx-auto max-h-[88vh] overflow-y-auto rounded-[16px]">
                                        <ProfessionalWithdrawalReceipt
                                          data={person}
                                          profile={profile}
                                          mode="screen"
                                          title="رسید برداشت"
                                          onClose={() =>
                                            toggleModal2(person.id)
                                          }
                                          onPublic={() => printReceipt(person)}
                                          onPrint={() =>
                                            printModalReceipt(person)
                                          }
                                        />
                                      </div>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      )}
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div style={{ width: "100%", height: "200px" }}>
          <PaginationBill
            meta={meta}
            pageCount={pageCount}
            setPageCount={setPageCount}
            setLoading={setLoading2}
            isDark={isDark}
          />
        </div>
      </div>


      {/* Print portals — مستقیم روی body تا فایرفاکس چند صفحه نسازد */}
      {(printData || printModalData) &&
        createPortal(
          <div className="print-receipt-portal">
            <style>
              {`
                @media print {
                  @page {
                    size: A5 portrait;
                    margin: 8mm;
                  }

                  html, body {
                    width: 100% !important;
                    height: auto !important;
                    min-height: 0 !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    background: #fff !important;
                    overflow: visible !important;
                    direction: ltr !important;
                  }

                  body.printing-receipt > *:not(.print-receipt-portal) {
                    display: none !important;
                  }

                  .print-receipt-portal {
                    display: flex !important;
                    justify-content: center !important;
                    align-items: flex-start !important;
                    width: 100% !important;
                    margin: 0 auto !important;
                    padding: 0 !important;
                    background: #fff !important;
                    direction: ltr !important;
                    text-align: center !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    color-adjust: exact !important;
                  }

                  .print-receipt-sheet {
                    width: 300px !important;
                    max-width: 300px !important;
                    margin: 0 auto !important;
                    float: none !important;
                    position: relative !important;
                    left: auto !important;
                    right: auto !important;
                    text-align: initial !important;
                    direction: rtl !important;
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                    page-break-after: avoid !important;
                    page-break-before: avoid !important;
                  }
                }

                @media screen {
                  .print-receipt-portal {
                    display: none !important;
                  }
                }
              `}
            </style>
            <div className="print-receipt-sheet">
              {printData && (
                <ProfessionalWithdrawalReceipt
                  data={printData}
                  profile={profile}
                  mode="public"
                  title="رسید عمومی برداشت"
                />
              )}
              {printModalData && (
                <ProfessionalWithdrawalReceipt
                  data={printModalData}
                  profile={profile}
                  mode="print"
                  title="رسید برداشت"
                />
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
